import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { DocumentType, graphql } from '../../gql';
import { useApiQuery } from '../../query/hooks';
import { DataPointTableRow } from './DataPointTableRow';
import { useModalContext } from '../../modal/ModalContext';
import { MetricDataPointWhere } from '../../gql/graphql';
import DatapointTable from '../../components/Table/Table';

const getMetricQuery = graphql(`
	query dataPointTable_getMetric($id: UUID!) {
		getContentMetric(by: { id: $id }) {
			metric {
				dataSet {
					id
				}
				dataLabels {
					id
				}
			}
		}
	}
`);

const listMetricDataPointQuery = graphql(`
	query dataPointTable_listMetricDataPoint($filter: MetricDataPointWhere!) {
		listContentMetricDataPoint(filter: { metricDataPoint: $filter }, orderBy: [{ metricDataPoint: { start: desc } }]) {
			id
			metricDataPoint {
				id
				value
				start
				end
			}
			creator {
				avatar {
					url(width: 24, height: 24)
				}
				name
			}
			createdAt
		}
	}
`);

type ContentMetricDataPoint = DocumentType<typeof listMetricDataPointQuery>['listContentMetricDataPoint'][number];

type Props = Readonly<{ contentMetricId: string }>;

const hasMetricDataPoint = (
	contentMetricDataPoint: ContentMetricDataPoint,
): contentMetricDataPoint is Omit<ContentMetricDataPoint, 'metricDataPoint'> & {
	metricDataPoint: NonNullable<ContentMetricDataPoint['metricDataPoint']>;
} => {
	return contentMetricDataPoint.metricDataPoint != null;
};

const DataPointTable: FC<Props> = ({ contentMetricId }) => {
	const { data: metricResult, isLoading: isMetricLoading } = useApiQuery(getMetricQuery, {
		variables: {
			id: contentMetricId,
		},
	});

	const filter: MetricDataPointWhere = {
		dataSet: {
			id: {
				eq: metricResult?.getContentMetric?.metric?.dataSet?.id,
			},
		},
		and: metricResult?.getContentMetric?.metric?.dataLabels.map((dataLabel) => ({
			dataLabels: {
				id: {
					eq: dataLabel.id,
				},
			},
		})),
	};

	const { data: dataPointsResult, isLoading: areDataPointsLoading } = useApiQuery(listMetricDataPointQuery, {
		enabled: metricResult != null,
		variables: { filter },
	});

	const { setModal } = useModalContext();

	const isLoading = isMetricLoading || areDataPointsLoading;
	const contentDataPoints = dataPointsResult?.listContentMetricDataPoint ?? [];

	const handleUpdate = (contentMetricDataPointId: string) => {
		setModal({ type: 'editMetricDataPoint', payload: { contentMetricDataPointId }, maxWidth: 'xs' });
	};

	const handleDelete = (contentMetricDataPointId: string) => {
		setModal({ type: 'deleteMetricDataPoint', payload: { contentMetricDataPointId } });
	};

	const tableHeader = [
		{
			text: '',
		},
		{
			text: <FormattedMessage id={'connections.metric-data-points.table.column.added'} />,
		},
		{
			text: <FormattedMessage id={'connections.metric-data-points.table.column.date-range'} />,
		},
		{
			text: <FormattedMessage id={'connections.metric-data-points.table.column.data'} />,
		},
		{
			text: '',
		},
	];

	const rows = contentDataPoints.filter(hasMetricDataPoint).map((contentDataPoint) => {
		return (
			<DataPointTableRow
				key={contentDataPoint.id}
				contentMetricDataPoint={contentDataPoint}
				onUpdate={() => handleUpdate(contentDataPoint.id)}
				onDelete={() => handleDelete(contentDataPoint.id)}
			/>
		);
	});

	return <DatapointTable variant={'withoutTitle'} isLoading={isLoading} headerData={tableHeader} tableRows={rows} />;
};

export { type ContentMetricDataPoint, DataPointTable };
